import { useId, useState, type SelectHTMLAttributes } from 'react';
import styles from './Filter.module.css';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'size'> {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  hasError?: boolean;
  size?: 'sm' | 'md' | 'lg';
  width?: string;
}

export function Filter({
  label,
  value,
  onChange,
  options,
  hasError = false,
  size = 'md',
  width = '200px',
  className,
  id,
  disabled,
  ...props
}: FilterProps) {
  const generatedId = useId();
  const selectId = id || generatedId;
  const [isFocused, setIsFocused] = useState(false);
  
  const hasValue = value !== undefined && value !== '';
  const isLabelActive = Boolean(label) && (isFocused || hasValue);

  const containerClasses = [
    styles.container,
    hasError ? styles.error : '',
    isFocused && !hasError ? styles.focused : '',
    disabled ? styles.disabled : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const selectClasses = [
    styles.select,
    styles[size],
    label ? styles.hasLabel : '',
    isLabelActive ? styles.labelActive : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses} style={{ width }}>
      <div className={styles.selectWrapper}>
        {label && (
          <label
            htmlFor={selectId}
            className={[
              styles.label,
              isLabelActive ? styles.labelActive : '',
              hasError ? styles.labelError : '',
              isFocused && !hasError ? styles.labelFocused : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {label}
          </label>
        )}

        <select
          id={selectId}
          className={selectClasses}
          disabled={disabled}
          value={value || ''}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={hasError}
          {...props}
        >
          <option className={styles.option} value="" disabled hidden></option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        
        <div className={styles.arrowIcon}>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

Filter.displayName = 'Filter';
